/**
 * ErrorBoundary — iter-264e (Feb 2026)
 *
 * A resilient wrapper for pages that may crash on unexpected data.
 * Instead of a black screen (React unmounts the whole tree when an
 * error propagates), we show a bilingual "something went wrong" panel
 * with the error text so the operator can retry / navigate away.
 *
 * Usage:
 *   <ErrorBoundary>
 *     <AgentCompanyDetailPage />
 *   </ErrorBoundary>
 */
import React from 'react';
import { AlertTriangle, RefreshCcw, ArrowLeft } from 'lucide-react';
import { Button } from './ui/button';

export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info);
  }

  reset = () => this.setState({ hasError: false, error: null });

  render() {
    if (!this.state.hasError) return this.props.children;
    const isAr = (typeof document !== 'undefined' ? document.documentElement.dir : 'ltr') === 'rtl';
    const msg = String(this.state.error?.message || this.state.error || '');
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6" data-testid="error-boundary">
        <div className="max-w-lg w-full rounded-2xl border border-rose-500/30 bg-rose-500/5 p-6 text-center">
          <div className="w-12 h-12 rounded-full bg-rose-500/15 border border-rose-500/40 flex items-center justify-center mx-auto mb-3">
            <AlertTriangle className="w-6 h-6 text-rose-300" />
          </div>
          <h2 className="text-lg font-bold text-white mb-1">
            {isAr ? 'حدث خطأ غير متوقع' : 'Something went wrong'}
          </h2>
          <p className="text-sm text-white/60 mb-4">
            {isAr
              ? 'تعطّلت هذه الصفحة أثناء عرض البيانات. يمكنك المحاولة مرة أخرى أو العودة للخلف.'
              : 'This page crashed while rendering data. You can retry or go back.'}
          </p>
          {msg && (
            <pre className="text-[11px] bg-black/40 border border-white/10 rounded-lg p-2 mb-4 text-rose-200 text-start overflow-auto max-h-40">
              {msg}
            </pre>
          )}
          <div className="flex items-center justify-center gap-2">
            <Button
              onClick={this.reset}
              className="bg-rose-600 hover:bg-rose-700 text-white gap-2"
              data-testid="error-boundary-retry"
            >
              <RefreshCcw className="w-4 h-4" />
              {isAr ? 'إعادة المحاولة' : 'Retry'}
            </Button>
            <Button
              onClick={() => window.history.back()}
              variant="outline"
              className="border-white/10 text-white/80 gap-2"
              data-testid="error-boundary-back"
            >
              <ArrowLeft className="w-4 h-4" />
              {isAr ? 'رجوع' : 'Back'}
            </Button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
